import _ from 'underscore';
import loadMapsApi from './loadMapsApi';

let sessionToken = null;

/**
 * Get the current autocomplete session token.
 * This token is used so we are only billed once per autocomplete session.
 * @param {boolean} refresh - Mark this token as used, so that it will be refreshed
 * (a new one will be generated) on the next autocomplete request
 * @see https://developers.google.com/places/web-service/session-tokens
 */
export function getAutocompleteSessionToken(refresh) {
  const token = sessionToken;
  if (refresh) {
    sessionToken = null;
  }
  return token;
}

/**
       * Determines whether the required google fields are present in the address object
       * @param   {Object}  components Address components from google
       * @param   {String}  streetAddress Street address from autocomplete api
       * @returns {Boolean}
       */
const isCompleteAddress = (components, streetAddress) => {
  const requiredFields = ['route', ['locality', 'sublocality_level_1'], 'administrative_area_level_1', 'postal_code'];
  const hasRequiredFields = _.every(requiredFields, field => _.some(components, component => {
    if (Array.isArray(field)) {
      // Check if the address has a city
      return _.some(field, subField => _.contains(component.types, subField));
    }
    // Check for street, state and zipcode
    return _.contains(component.types, field);
  }));
  return hasRequiredFields && /^\d+/.test(streetAddress);
};

const getAddressComponent = function (components, name, abbreviated) {
  const abbr = abbreviated ? 'short_name' : 'long_name';

  const componentObj = components.filter(component => _.contains(component.types, name))[0];

  if (componentObj) {
    return componentObj[abbr];
  }
  return null;
};

/**
 * Maps google address to UI address
 * @param   {Object} address Google address components
 * @param   {String} streetAddress Street address from autocomplete api
 * @returns {Object}         UI address object
 */
const getAddressData = (address, streetAddress) => ({
  location_address1: streetAddress,
  location_city: getAddressComponent(address, 'locality') || getAddressComponent(address, 'sublocality_level_1'),
  location_state: getAddressComponent(address, 'administrative_area_level_1', true),
  location_zipcode: getAddressComponent(address, 'postal_code', false),
});

/**
 * Uses the placeId to pull full address information using the PlacesService
 * @param   {String} placeId    placeId corresponding to a google address
 * @param   {Object} mapElement The API requires a map object to make the request.
 *                               It can be hidden.
 * @param   {String} streetAddress Street address from autocomplete api
 * @returns {Object}            Promise object with mapped address data or error keys
 */
export async function getFullAddress(placeId, mapElement, streetAddress) {
  let resolve;
  let reject;
  const google = await loadMapsApi();
  const service = new google.maps.places.PlacesService(mapElement);

  const result = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });

  service.getDetails({
    placeId,
  }, (place, status) => {
    if (status !== google.maps.places.PlacesServiceStatus.OK) {
      reject('googleError');
    } else if (
      streetAddress.length
      && !isCompleteAddress(place.address_components, streetAddress)
    ) {
      reject('googleAddressError');
    } else {
      const addressObj = getAddressData(place.address_components, streetAddress);
      resolve(addressObj);
    }
  });

  return result;
}

/**
 * Returns a list of places in the world which, in some way, match the input string.
 *
 * @param   {String}  input String to autocomplete
 * @param   {Array<String>} types The types of places to find. See Google Maps API documentation.
 * @returns {Object}  List of results in the form of an AutocompletePrediction
 * @see https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletePrediction
 */
export default function locationAutocomplete(input, types = ['geocode']) {
  return new Promise((resolve, reject) => {
    loadMapsApi()
      .then(google => {
        const autocompleteService = new google.maps.places.AutocompleteService();

        // Generate new token
        if (!sessionToken) {
          sessionToken = new google.maps.places.AutocompleteSessionToken();
        }

        autocompleteService.getPlacePredictions({
          input,
          types,
          sessionToken,
          componentRestrictions: { country: 'us' },
        }, (results, status) => {
          if (status === 'OK') {
            resolve(results.map(result => {
              // eslint-disable-next-line no-param-reassign
              result.description = result.description.replace(/, USA?$/, '');
              return result;
            }));
          } else {
            reject(new Error(`An error occurred finding search results: ${status}`));
          }
        });
      })
      .catch(reject);
  });
}
